import { defineStore } from 'pinia';
import axios from 'axios';

import { micromark } from 'micromark';
import { gfm, gfmHtml } from 'micromark-extension-gfm';

import { useAppStore } from './app';

import groupStore from './group';

import { getVariantsByIds } from '../helpers/shopify';

import { formatPrice } from '../helpers/format';

import emitter from '../helpers/bus';

import * as constants from '../constants';

const initialState = {
    // Widget configuration options.
    options: {},

    // Currently selected controller.
    viewController: null,

    // Selected view.
    selectedView: null,

    // Suppress thumbnails cause we are doing something.
    suppressThumbnails: false,

    // Force view selection.
    selectedViewCounter: 0,

    // Default unit price.
    defaultUnitPrice: null,

    // Subtotal unit price.
    subtotalUnitPrice: null,

    // Default subtotal price.
    defaultSubtotalPrice: null,

    // Number of customizations.
    personalizations: [],

    // Customizations price.
    customizationsPrice: null,

    // Help panel open.
    isHelpPanelOpen: false,

    // Start over confirmation modal open.
    isStartOverConfirmationModalOpen: false,

    // Quit confirmation modal open.
    isQuitConfirmationModalOpen: false,

    // Review confirmation modal open.
    isReviewConfirmationModalOpen: false,

    // Need to review groups.
    isReviewGroupTextModalOpen: false,

    // Show bulk order info modal.
    isEnableBulkInformationModalOpen: false,

    // Backup controller.
    backupController: null,

    // Product data.
    products: [],

    // Allowed SKUs.
    // A map of category -> list.
    allowedSkus: {},

    // Number of items.
    quantity: 1,

    // Use this SKU instead.
    cartSku: null,

    // Did we load from a recipe?
    loadedFromRecipe: false,

    // Currently select group personalization type.
    currentGroupPersonalizationType: null,

    // Group personalization data, or empty if disabled.
    groupPersonalization: null,

    // Selected group item (item itself, not the index).
    selectedGroupItem: null,
};

// eslint-disable-next-line import/prefer-default-export
export const useCustomizerStore = defineStore('customizer', {
    id: 'customizer',

    state: () => ({ ...initialState }),

    getters: {
        /**
         * Gets a list of available personalization types.
         */
        availablePersonalizationTypes() {
            const colors = this.viewController?.findPlacement('ProductColor');

            const list = [];

            if (colors == null) {
                return list;
            }

            const options = this.viewController.groupByDefinitions(colors);
            const placement = options.find((x) => x.description.definition === 'Product Component');

            if (placement == null) {
                return [];
            }

            for (const type of placement.options) {
                if (type.allowed && type.available) {
                    list.push({
                        text: type.option.name,
                        definition: type.option.definition,
                        value: type.option.code,
                        selected: type.selected,
                        hex: type.option.custom?.hex,
                        pattern: type.option.custom?.['pattern-image'],
                        selectable: type,
                    });
                }
            }

            return list;
        },

        activeProductType() {
            const activeType = this.availablePersonalizationTypes?.find((x) => x.selected)?.value;

            if (activeType?.includes('Wallet')) {
                return constants.ProductType.WALLET;
            }

            if (activeType?.includes('Ring')) {
                return constants.ProductType.RING;
            }

            return activeType;
        },

        /**
         * Gets a list of available personalization colors.
         */
        availablePersonalizationColors() {
            const colors = this.viewController?.findPlacement('ProductColor');
            const selected = this.viewController.selectedAtPlacement('ProductColor');
            const selectedType = selected.description.find((x) => x.definition === 'Product Component');

            const list = [];

            if (colors == null) {
                return list;
            }

            const options = this.viewController.groupByDefinitions(colors);
            const placement = options.find((x) => x.description.definition === 'Item Color');

            if (placement == null) {
                return [];
            }

            for (const type of placement.options) {
                if (type.allowed && type.available) {
                    const matchesType = type.components.some((x) => x.component.description.some((y) => y.code === selectedType.code));

                    if (matchesType) {
                        list.push({
                            text: type.option.name,
                            definition: type.option.definition,
                            value: type.option.code,
                            selected: type.selected,
                            hex: type.option.custom?.hex,
                            pattern: type.option.custom?.['pattern-image'],
                            selectable: type,
                        });
                    }
                }
            }

            return list;
        },

        /**
         * Gets a list of available personalization sizes.
         */
        availablePersonalizationSizes() {
            const colors = this.viewController?.findPlacement('ProductColor');
            const selected = this.viewController.selectedAtPlacement('ProductColor');

            const selectedType = selected.description.find((x) => x.definition === 'Product Component');
            const selectedColor = selected.description.find((x) => x.definition === 'Item Color');

            const list = [];

            if (colors == null) {
                return list;
            }

            const options = this.viewController.groupByDefinitions(colors);
            const placement = options.find((x) => x.description.definition === 'Item Size');

            if (placement == null) {
                return [];
            }

            for (const type of placement.options) {
                if (type.allowed && type.available) {
                    const matchesType = type.components.some((x) => x.component.description.some((y) => y.code === selectedType.code)
                        && x.component.description.some((y) => y.code === selectedColor.code));

                    if (matchesType) {
                        list.push({
                            text: type.option.name,
                            definition: type.option.definition,
                            value: type.option.code,
                            selected: type.selected,
                            hex: type.option.custom?.hex,
                            pattern: type.option.custom?.['pattern-image'],
                            selectable: type,
                        });
                    }
                }
            }

            return list;
        },

        /**
         * Get a list of all available icons, sorted by category.
         */
        iconsByCategory() {
            const icons = this.viewController?.findPlacement('ImageGallery');

            const list = [];

            if (icons == null) {
                return list;
            }

            for (const icon of icons.components) {
                if (icon.placeholder) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Skip covers.
                if (icon.custom?.cover === 'true') {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                if (!this.viewController.isAvailable(icon)) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                const category = icon.custom.category;
                if (category == null) {
                    // eslint-disable-next-line no-continue
                    continue;
                }

                let categoryObject = list.find((x) => x.category === category);
                if (categoryObject == null) {
                    categoryObject = {
                        category,
                        icons: [],
                    };

                    list.push(categoryObject);
                }

                const image = `${icon.custom.image}?scl=2&fmt=webp&qlt=60`;

                let iconImage = null;

                if (icon.custom['icon-image']) {
                    iconImage = `${icon.custom['icon-image']}?scl=2&fmt=webp&qlt=60`;
                }

                // Can we switch to this icon?
                if (this.loadedFromRecipe) {
                    // Specific cart SKU is selected, must use the same.
                    if (this.cartSku && (!icon.custom['cart-sku'] || icon.custom['cart-sku'] !== this.cartSku)) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    // Specific cart SKU is not selected, but icon requires it.
                    if (!this.cartSku && icon.custom['cart-sku']) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }
                }

                // Check if text editing is available for this specific icon
                const hasTextEditAvailable = icon.tags.includes('has-text');

                categoryObject.icons.push({
                    icon: icon.code,
                    size: 100,
                    image,
                    iconImage,
                    source: 'gallery',
                    name: icon.description[0].name,
                    hasTextEditAvailable,
                });
            }

            return list.filter((category) => category.icons.length > 0);
        },

        /**
         * Get the selected icon code.
         */
        selectedIcon() {
            const selected = this.viewController?.selectedAtPlacement('DesignLogo');

            if (selected == null) {
                return null;
            }

            return selected.custom?.['dynamic-image-gallery'];
        },

        /**
         * Text options.
         */
        textOptions() {
            const placement = this.viewController?.findPlacement('Text');

            const definitions = this.viewController.groupByDefinitions(placement);

            return definitions;
        },

        /**
         * Are there any icons?
         */
        anyIconOptions() {
            const placement = this.viewController?.findPlacement('DesignLogo');

            if (placement == null) {
                return false;
            }

            return true;
        },

        /**
         * Are there any selectable text options?
         */
        anyTextOptions() {
            const placement = this.viewController?.findPlacement('Text');

            if (placement == null) {
                return false;
            }

            const anyOptions = placement.components.find((x) => !x.placeholder && this.viewController.isAvailable(x));

            return anyOptions != null;
        },

        /**
         * Monogramming options.
         */
        monogramOptions() {
            const placement = this.viewController?.findPlacement('Monogram');

            const definitions = this.viewController.groupByDefinitions(placement);

            return definitions;
        },

        /**
         * Are there any selectable monogramming options?
         */
        anyMonogramOptions() {
            const placement = this.viewController?.findPlacement('Monogram');

            if (placement == null) {
                return false;
            }

            const anyOptions = placement.components.find((x) => !x.placeholder && this.viewController.isAvailable(x));

            return anyOptions != null;
        },

        /**
         * Format 0.00 price.
         */
        zeroPrice() {
            const value = formatPrice(0, false, this.options.locale, this.options.currency, {
                zeroEmpty: false,
            });

            return value;
        },

        // Ridge specific.

        /**
         * Monogramming type based on the first item personalization.
         */
        targetGroupPersonalizationType() {
            const currentText = this.viewController?.selectedAtPlacement('Text');

            const text = currentText?.custom?.['personalization-text'] ?? '';

            if (text.length > 0) {
                return {
                    type: 'text',
                    code: currentText.code,
                };
            }

            const currentMonogram = this.viewController?.selectedAtPlacement('Monogram');

            const monogram = currentMonogram?.custom?.['personalization-text'] ?? '';

            if (monogram.length > 0) {
                return {
                    type: 'monogram',
                    code: currentMonogram.code,
                };
            }

            return null;
        },
    },

    actions: {
        /**
         * Save options.
         */
        setOptions(options) {
            this.options = options;

            // Replace empty values that we are getting from SFCC with proper nulls.
            if (this.options.products === '') {
                this.options.products = null;
            }

            if (this.options.package === '') {
                this.options.package = null;
            }

            emitter.$on('afterSceneChanged', this.sceneUpdated);
        },

        getStatePrice(controller, options) {
            const finalOptions = {
                ...{
                    ignoreOptional: true,

                    fixedPlacements: ['ProductColor'],
                },
                ...options,
            };

            const price = controller.statePrice(finalOptions);

            return price;
        },

        setPrices({
            defaultUnitPrice,
            subtotalUnitPrice,
            defaultSubtotalPrice,
            customizationsPrice,
        }) {
            this.defaultUnitPrice = defaultUnitPrice;
            this.subtotalUnitPrice = subtotalUnitPrice;
            this.defaultSubtotalPrice = defaultSubtotalPrice;
            this.customizationsPrice = customizationsPrice;
        },

        updatePrice: async () => { },

        /**
         * Start loading products.
         */
        load(options) {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            if (options) {
                Object.assign(this.options, options);
            }

            if (this.options.recipe) {
                const timestamp = new Date().getTime();

                // Get a recipe first.
                let recipeUrl = `https://cz.drrv.co/recipe/${this.options.recipe}.json?timestamp=${timestamp}`;

                fetch(recipeUrl).then((response) => response.json()).then((data) => {
                    this.loadController(data);
                }).catch(() => {
                    recipeUrl = `https://customizerstoragenightly.blob.core.windows.net/recipe/${this.options.recipe}.json?timestamp=${timestamp}`;

                    fetch(recipeUrl).then((response) => response.json()).then((data) => {
                        this.loadController(data);
                    }).catch(() => {
                        this.loadController();
                    });
                });
            } else {
                this.loadController();
            }
        },

        /**
         * Load controller and initialize from recipe.
         */
        loadController(fromRecipe) {
            const controller = new this.options.DriveCustomizer.Controller({
                ...this.getDefaultControllerOptions(fromRecipe),
            });

            // controller.traceAllowed = true;
            // controller.traceAvailable = true;
            controller.traceUpdated = true;

            controller.on('initializeAvailability', () => this.loadProductData(controller));

            controller.on('isAllowed', (component) => this.isAllowed(component));

            controller.on('isAvailable', (component) => this.isAvailable(component));

            controller.on('getPrice', (component) => this.getPrice(component));

            controller.on('afterInitializeBlueprint', () => {
                this.continueLoading(controller, fromRecipe);
            });

            controller.load();
        },

        /**
         * Check if component is in stock.
         */
        isAvailable(component) {
            // Special case, if cart SKU is selected,
            // need to allow the base SKU.
            if (this.allowedSkus?.gallery?.length > 0) {
                if (component.sku) {
                    return this.allowedSkus.gallery.includes(component.sku);
                }
            }

            // Does it have a UPC?
            if (component.upc && this.products) {
                const product = this.products.find((x) => x.variant === component.upc);

                if (product) {
                    if (this.options.alwaysAvailable) {
                        return true;
                    }

                    return product.ats > 0;
                }

                if (this.options.alwaysAvailable) {
                    return true;
                }

                return false;
            }

            // Is a gallery icon that wants a specific SKU?
            if (component.custom['cart-sku']) {
                const sku = component.custom['cart-sku'];

                const product = this.products.find((x) => x.sku === sku);

                if (product) {
                    if (this.options.alwaysAvailable) {
                        return true;
                    }

                    return product.ats > 0;
                }

                return false;
            }

            return null;
        },

        /**
         * Check if component is allowed.
         */
        isAllowed(component) {
            if (this.viewController == null) {
                return null;
            }

            // Only apply sku based restrictions to colors.
            const placement = this.viewController.findComponentPlacement(component.code);

            if (placement == null || placement.code !== 'ProductColor') {
                return null;
            }

            // SKU restrictions.
            const sku = component.description.find((x) => x.sku)?.sku;

            if (sku) {
                const allAllowed = [];

                for (const list of Object.values(this.allowedSkus)) {
                    allAllowed.push(...list);
                }

                if (allAllowed.length > 0) {
                    if (!allAllowed.includes(sku)) {
                        return false;
                    }
                }

                if (this.allowedSkus?.gallery?.length > 0) {
                    // Special case, allow SKU based on gallery choices.
                    if (this.allowedSkus.gallery.includes(sku)) {
                        return true;
                    }
                }
            }

            if (this.options.alwaysAvailable == null || this.options.alwaysAvailable === false) {
                // Products should have prices.
                if (component.upc) {
                    const price = this.getPrice(component);

                    if (price == null) {
                        return false;
                    }
                } else {
                    return false;
                }
            }

            // Return null to default to rules.
            return null;
        },

        /**
         * Get component price.
         */
        getPrice(component) {
            // If this component is specific cart SKU, we need to pick that price.
            const placement = this.viewController.findComponentPlacement(component.code);

            if (placement.code === 'ProductColor') {
                if (this.cartSku && this.products) {
                    const product = this.products.find((x) => x.sku === this.cartSku);

                    if (product) {
                        return product.price;
                    }
                }
            }

            if (component.upc && this.products) {
                const product = this.products.find((x) => x.variant === component.upc);

                if (product) {
                    return product.price;
                }
            }

            return null;
        },

        /**
         * Do updates after state changed.
         */
        stateChanged() {
            this.calculatePrice();
        },

        /**
         * Update prices.
         */
        calculatePrice() {
            if (this.viewController) {
                let quantity = 1;

                if (this.quantity) {
                    quantity = this.quantity;
                }

                if (this.groupPersonalization?.length > 0) {
                    quantity = this.groupPersonalization.length;
                }

                const price = this.getStatePrice(this.viewController);

                this.statePrice = price;

                let mainPrice = 0;
                let customizationsPrice = 0;

                for (const placement of price.breakdown) {
                    if (placement.placement.startsWith('Upcharge')) {
                        customizationsPrice += placement.current * quantity;
                    } else {
                        mainPrice += placement.current * quantity;
                    }
                }

                const total = price.current * quantity;

                this.setPrices({
                    defaultUnitPrice: formatPrice(mainPrice, false, this.options.locale, this.options.currency),
                    customizationsPrice: formatPrice(customizationsPrice, false, this.options.locale, this.options.currency),
                    defaultSubtotalPrice: formatPrice(total, false, this.options.locale, this.options.currency),
                });
            }
        },

        /**
         * Load product data from Shopify.
         */
        async loadProductData(controller) {
            // Make a list of SKUs.
            const upcs = new Set();
            const skus = new Set();

            const colors = controller.findPlacement('ProductColor');

            for (const color of colors.components) {
                if (color.upc) {
                    upcs.add(color.upc);
                }
            }

            // Also upcharges.
            const upcharges = controller.findPlacement('UpchargeFront');

            for (const upcharge of upcharges.components) {
                if (upcharge.upc) {
                    upcs.add(upcharge.upc);
                }
            }

            // Also add from the gallery.
            const gallery = controller.findPlacement('ImageGallery');

            for (const image of gallery.components) {
                const onlySku = image.custom['only-skus'];
                if (onlySku) {
                    skus.add(onlySku);
                }

                const cartSku = image.custom['cart-sku'];
                if (cartSku) {
                    skus.add(cartSku);
                }
            }

            // First map necessary SKUs.

            const mappedUpcs = await controller.skusToUpcs([...skus]);

            for (const sku of skus) {
                if (mappedUpcs[sku]) {
                    upcs.add(mappedUpcs[sku]);
                }
            }

            const results = await getVariantsByIds(controller.blueprint, [...upcs], {
                storefrontDomain: this.options.storefrontDomain,
                storefrontAccessToken: this.options.storefrontAccessToken,
            });

            this.products = results;
        },

        /**
         * Calculate default controller options.
         */
        getDefaultControllerOptions() {
            const options = {
                apiKey: this.options.apiKey,
                products: this.options.products,
                preview: this.options.preview,
                site: this.options.site,
                version: this.options.version,
                locale: this.options.locale,
                disableCaching: this.options.disableCaching,
            };

            if (this.options.endpoint) {
                options.endpoint = this.options.endpoint;
            }

            return options;
        },

        /**
         * Perform late-stage controller initialization.
         */
        // eslint-disable-next-line no-unused-vars
        continueLoading(controller, fromRecipe) {
            this.attachControllerEvents(controller);

            if (fromRecipe) {
                let loadedFromRecipe = true;

                if (this.options.allowEditFromRecipe) {
                    loadedFromRecipe = false;
                }

                for (const placement of fromRecipe.components) {
                    if (placement.custom['skip-from-recipe'] === 'true') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    const controllerPlacement = controller.findPlacement(placement.code);

                    if (controllerPlacement == null) {
                        loadedFromRecipe = false;
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (controllerPlacement.custom['skip-from-recipe'] === 'true') {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (placement.addedTags) {
                        for (const tag of placement.addedTags) {
                            controller.addPlacementTag(placement.code, tag);
                        }
                    }

                    if (placement.component.code) {
                        const controllerComponent = controllerPlacement.components.find((c) => c.code === placement.component.code);

                        if (controllerComponent && controller.isAvailable(controllerComponent)) {
                            controller.updateComponent(placement.code, controllerComponent.code);

                            // Copy custom attributes.
                            if (placement.component.custom) {
                                if (!controllerComponent.custom) {
                                    controllerComponent.custom = {};
                                }

                                Object.assign(controllerComponent.custom, placement.component.custom);
                            }

                            // RIDGE!
                            if (controllerComponent.custom?.['dynamic-image-gallery']) {
                                const imageComponent = controller.findComponent(controllerComponent.custom?.['dynamic-image-gallery']);

                                if (imageComponent) {
                                    // Apply SKU restrictions.
                                    if (imageComponent.custom['only-skus']) {
                                        this.allowedSkus.gallery = [imageComponent.custom['only-skus']];
                                    } else {
                                        this.allowedSkus.gallery = [];
                                    }

                                    // RIDGE: Check for cart SKU.
                                    if (imageComponent.custom['cart-sku']) {
                                        this.cartSku = imageComponent.custom['cart-sku'];
                                    } else {
                                        this.cartSku = null;
                                    }
                                }
                            }

                            if (placement.component.addedTags) {
                                for (const tag of placement.component.addedTags) {
                                    controller.addComponentTag(controllerComponent.code, tag);
                                }
                            }

                            controller.updateComponents();
                        } else {
                            // eslint-disable-next-line no-console
                            console.log(`Component ${placement.component.code} is not longer available`);

                            loadedFromRecipe = false;
                        }
                    }
                }

                this.loadedFromRecipe = loadedFromRecipe;
            }

            this.viewController = controller;

            controller.updateComponents();

            const appStore = useAppStore();
            appStore.setIsBusy(false);

            // Select the initial step.
            appStore.setCurrentStep(constants.STEP_WALLET);

            // Just store the initial controller as backup
            this.backupController = controller.clone();

            // Should we select an icon?
            if (this.options.selectedIcon) {
                // Can we select icon?
                let canSelectIcon = true;

                const component = this.viewController.findComponent(this.options.selectedIcon);
                const product = Array.isArray(this.options.products) ? this.options.products[0] : this.options.products;

                // Configure the product with the selected icon.
                if (canSelectIcon && component && product) {
                    function extractSku(productConfig) {
                        const regex = /\(([^)]*)\)/;
                        const match = productConfig.match(regex);
                        return match ? match[1] : null;
                    }

                    const onlySku = component.custom['only-skus'];
                    if (onlySku) {
                        const onlySkus = onlySku.split(',').map((x) => x.trim()).filter((x) => x.length > 0);
                        const selectProduct = extractSku(product);
                        if (selectProduct && !onlySkus.includes(selectProduct)) {
                            canSelectIcon = false;
                        }
                    }

                    const cartSku = component.custom['cart-sku'];
                    if (cartSku) {
                        const selectProduct = extractSku(product);
                        if (selectProduct && selectProduct !== cartSku) {
                            canSelectIcon = false;
                        }
                    }

                    if (canSelectIcon) {
                        this.selectIcon(this.options.selectedIcon);
                    }
                }
            }
        },

        /**
         * Attaches events to the controller.
         */
        attachControllerEvents(controller) {
            controller.on('setReactive', (v) => {
                // Vue 3 doesn't need Vue.set anymore.
                v.target[v.key] = v.value;

                return true;
            });

            controller.on('stateChanged.viewController', () => {
                this.stateChanged();
            });
        },

        /**
         * Select view to be displayed.
         */
        selectView(view) {
            const lastView = this.selectedView;

            this.selectedView = view;

            if (lastView?.code === view?.code) {
                this.selectedViewCounter += 1;
            }
        },

        /**
         * The widget has done whatever it was doing and now it is time to close. Should we destroy the instance or just keep it
         * (for example if the host page redirects anyway).
         */
        globalDone() {
            const appStore = useAppStore();

            appStore.globalDone();
        },

        /**
         * The widget has done whatever it was doing and now it is time to close. Should we destroy the instance or just keep it
         * (for example if the host page redirects anyway).
         */
        globalCancel() {
            const appStore = useAppStore();

            appStore.globalCancel();
        },

        /**
         * Make a simple component selection.
         */
        commitSelection(code) {
            if (this.viewController) {
                const placement = this.viewController.findComponentPlacement(code);

                this.viewController.updateComponent(placement.code, code);
            }
        },

        /**
         * Upload an image, using a component configuration as guidance.
         */
        async uploadImage(file, target, options) {
            const formData = new FormData();

            if (file instanceof File) {
                formData.append('file', file);
            } else {
                formData.append('copy-from', file);
            }

            const imageMinHeight = target && target['dynamic-image-upload-limit-min-height'] ? +target['dynamic-image-upload-limit-min-height'] : null;
            const imageMaxHeight = target && target['dynamic-image-upload-limit-max-height'] ? +target['dynamic-image-upload-limit-max-height'] : null;

            const imageMinWidth = target && target['dynamic-image-upload-limit-min-width'] ? +target['dynamic-image-upload-limit-min-width'] : null;
            const imageMaxWidth = target && target['dynamic-image-upload-limit-max-width'] ? +target['dynamic-image-upload-limit-max-width'] : null;

            if (imageMinHeight && imageMinWidth) {
                formData.append('min-width', imageMinWidth);
                formData.append('min-height', imageMinHeight);
            }

            if (imageMaxHeight && imageMaxWidth) {
                formData.append('max-width', imageMaxWidth);
                formData.append('max-height', imageMaxHeight);
            }

            const fit = target && target['dynamic-image-upload-fit'] ? +target['dynamic-image-upload-fit'] : null;
            const fitWidth = target && target['dynamic-image-upload-fit-width'] ? +target['dynamic-image-upload-fit-width'] : null;
            const fitHeight = target && target['dynamic-image-upload-fit-height'] ? +target['dynamic-image-upload-fit-height'] : null;

            if (fit && fitWidth && fitHeight) {
                formData.append('fit', fit);
                formData.append('fit-width', fitWidth);
                formData.append('fit-height', fitHeight);
            }

            let trace = false;

            if (file instanceof File && options?.trace) {
                const fileName = file.name.toLowerCase();

                if (fileName.endsWith('.png') || fileName.endsWith('.jpg')) {
                    trace = true;
                }
            }

            if (trace) {
                formData.append('trace', 'true');

                formData.append('trace-threshold', '0.5');

                formData.append('bgc', 'ffffff');

                formData.append('minrasterwidth', '2000');
                formData.append('minrasterheight', '2000');
            } else {
                formData.append('min-raster-width', '2000');
                formData.append('min-raster-height', '2000');
            }

            formData.append('allow-reuse', 'true');

            try {
                const response = await axios({
                    method: 'post',
                    url: '//api.images.drivecommerce.com/api/v1/image/ridge/upload/',
                    data: formData,
                    config: {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    },
                    onUploadProgress: (progressEvent) => {
                        if (options?.onUploadProgress) {
                            options.onUploadProgress((progressEvent.loaded / progressEvent.total) * 100);
                        }
                    },
                });

                return {
                    image: response.data,
                    file,
                    target,
                    options,
                };
            } catch (error) {
                if (error?.response?.data) {
                    const { data } = error.response;

                    if (data.modelState?.['']?.length > 0) {
                        if (data.details?.error === 'maximum-dimensions') {
                            throw new Error({
                                error: data.details.error,
                            });
                        } else if (data.details?.error === 'font-outlines') {
                            throw new Error({
                                error: data.details.error,
                            });
                        } else {
                            throw new Error({
                                error: data.details.error,
                            });
                        }
                    }
                }

                throw error;
            }
        },

        /**
         * Apply dynamic image to a component.
         */
        async applyImage(imageCode, image, component) {
            const placement = this.viewController.findComponentPlacement(component.code);

            // Make the design all white.
            this.viewController.commitCustomAttributes(placement.code, {
                'effect-colorize': '000000',
                'effect-colorize-add': 'ffffff',
            });

            // Configure the image.
            this.viewController.updateDynamicImage(placement.code, {
                title: image.title,
                name: image.name,
                url: image.imageUrl,
                pngUrl: image.pngImageUrl,
                jpgUrl: image.jpgImageUrl,
                width: image.width,
                height: image.height,
                density: image.density,
            });

            // Keep track of what we have selected.
            this.viewController.commitCustomAttributes(placement.code, {
                'dynamic-image-gallery': imageCode,
            });

            const selected = this.viewController.selectedAtPlacement(placement.code);

            return selected;
        },

        /**
         * Select icon.
         */
        async selectIcon(imageCode) {
            const component = this.viewController.findComponent(imageCode);

            if (component == null) {
                return;
            }

            const targetPlacement = this.viewController.findPlacement('DesignLogo');

            if (targetPlacement == null) {
                return;
            }

            const targetComponent = this.viewController.selectedAtPlacement(targetPlacement.code);

            if (component) {
                const result = await this.uploadImage(component.custom.image, targetComponent);

                await this.applyImage(imageCode, result.image, targetComponent, component.tags);

                // Apply SKU restrictions.
                if (component.custom['only-skus']) {
                    this.allowedSkus.gallery = [component.custom['only-skus']];
                } else {
                    this.allowedSkus.gallery = [];
                }

                // RIDGE: Check for cart SKU.
                if (component.custom['cart-sku']) {
                    this.cartSku = component.custom['cart-sku'];
                } else {
                    this.cartSku = null;
                }

                // Apply tags from the gallery.
                this.viewController.clearPlacementTags(targetPlacement.code);
                this.viewController.addPlacementTag(targetPlacement.code, 'personalized');
                this.viewController.addPlacementTag(targetPlacement.code, 'personalized-with-icon');

                const applyTags = component.tags;

                if (applyTags) {
                    for (const tag of applyTags) {
                        this.viewController.addPlacementTag(targetPlacement.code, tag);
                    }
                }

                this.viewController.updateComponents();
            }
        },

        /**
         * Snapshot specific image for recipes.
         */
        processRecipeImage(viewCode, width, height, background, type) {
            return new Promise((resolve) => {
                emitter.$emit('snapshot', {
                    controllerId: this.viewController.id,

                    view: viewCode,

                    overrideWidth: width,
                    overrideHeight: height,

                    overrideBackground: background,

                    mimeType: type,

                    done: (image) => {
                        resolve(image);
                    },
                });
            });
        },

        async processRecipe(extraOptions) {
            const savedImages = {};

            for (const view of this.viewController.blueprint.views) {
                if (view.bakeInRecipe) {
                    if (view.custom?.['snapshot-from']) {
                        // eslint-disable-next-line no-await-in-loop
                        const image = await this.processRecipeImage(
                            view.custom['snapshot-from'],
                            +(view.custom['snapshot-width'] ?? '1600'),
                            +(view.custom['snapshot-height'] ?? '1600'),
                            view.custom['snapshot-background'] ?? '#00000000',
                            view.custom['snapshot-type'] ?? 'image/jpeg',
                        );

                        savedImages[view.code] = image;
                    }
                }
            }

            const result = await this.viewController.saveRecipe({
                ...(extraOptions ?? {}),
                translateRecipeAfter: (recipe) => {
                    if (this.options.designRequest) {
                        recipe.designRequest = this.options.designRequest;
                    }

                    if (recipe.views) {
                        for (const view of recipe.views) {
                            if (savedImages[view.code]) {
                                view.clientRender = savedImages[view.code];
                            }
                        }
                    }
                },
            });

            return result;
        },

        /**
         * Save recipe for preview purposes.
         */
        async getPreviewImages() {
            const { location } = await this.processRecipe({
                temporaryRecipe: true,
            });

            const recipe = await fetch(location).then((res) => res.json());

            const previewFront = recipe?.views?.find((x) => x.code === 'PreviewFront');

            if (!previewFront) {
                // eslint-disable-next-line no-console
                console.error('No preview found');

                return null;
            }

            const { preview, previewJpg, previewPng } = previewFront;

            return {
                recipe,
                preview,
                previewJpg,
                previewPng,
            };
        },

        sceneUpdated(status) {
            this.sceneLoading = status.loading;
        },

        async nextTick() {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve();
                });
            });
        },

        async nextWhile(check) {
            return new Promise((resolve) => {
                const doCheck = () => {
                    if (!check()) {
                        resolve();
                    }

                    setTimeout(() => {
                        doCheck();
                    }, 10);
                };

                doCheck();
            });
        },

        /**
         * Save a batch of recipes.
         */
        async saveBatch() {
            // First, we want to preprocess all recipes.
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.suppressThumbnails = true;

            // Show poster to prevent stuff from showing.
            emitter.$emit('capturePoster', {
                duration: 100000000,
                force: true,
                preventRelease: true,
            });

            const recipes = [];

            for (const item of this.groupPersonalization) {
                this.syncGroupPersonalization(item);

                // We need to wait until textures are done loading.
                // eslint-disable-next-line no-await-in-loop
                await this.nextTick();

                // eslint-disable-next-line no-console
                console.log('Waiting for loading...', this.sceneLoading);

                // eslint-disable-next-line no-await-in-loop
                await this.nextWhile(() => this.sceneLoading);

                // eslint-disable-next-line no-console
                console.log('Done...');

                // eslint-disable-next-line no-await-in-loop
                const recipe = await this.processRecipe({
                    translateRecipe: (data) => {
                        data.custom.quantity = '1';

                        if (this.cartSku) {
                            data.custom['cart-sku'] = this.cartSku ?? '';

                            const product = this.products.find((x) => x.sku === this.cartSku);

                            data.custom['cart-upc'] = product.variant;
                        }
                    },
                });

                recipes.push(recipe);
            }

            if (this.options.onAddToCart) {
                this.options.onAddToCart(recipes);
            }

            appStore.setIsBusy(false);

            this.syncGroupPersonalization(this.selectedGroupItem);

            this.globalDone();
        },

        /**
         * Save recipe.
         */
        async saveRecipe() {
            if (this.groupPersonalization != null && this.groupPersonalization.length > 1) {
                await this.saveBatch();
                return;
            }

            const appStore = useAppStore();

            appStore.setIsBusy(true);

            const recipe = await this.processRecipe({
                translateRecipe: (data) => {
                    data.custom.quantity = (this.quantity || 1).toString();

                    if (this.cartSku) {
                        data.custom['cart-sku'] = this.cartSku ?? '';

                        const product = this.products.find((x) => x.sku === this.cartSku);

                        data.custom['cart-upc'] = product.variant;
                    }
                },
            });

            if (this.options.onAddToCart) {
                this.options.onAddToCart([recipe]);
            }

            appStore.setIsBusy(false);

            this.globalDone();
        },

        /**
         * Reset all customizations
         */
        resetAll() {
            this.viewController = this.backupController.clone();

            this.viewController.updateComponents();

            this.viewController.stateChanged();

            this.cartSku = null;

            this.allowedSkus = {};

            const appStore = useAppStore();

            appStore.setCurrentStep(constants.STEP_WALLET);
        },

        resetBodyStep() {
            const oldSelectedColor = this.backupController.selectedAtPlacement('ProductColor');

            if (oldSelectedColor) {
                this.viewController.updateComponent('ProductColor', oldSelectedColor.code);
            }

            this.viewController.stateChanged();
        },

        // Open help panel.
        openHelpPanel() {
            this.isHelpPanelOpen = true;
        },

        closeHelpPanel() {
            this.isHelpPanelOpen = false;
        },

        toggleHelpPanel() {
            this.isHelpPanelOpen = !this.isHelpPanelOpen;
        },

        toggleStartOverConfirmationModal() {
            this.isStartOverConfirmationModalOpen = !this.isStartOverConfirmationModalOpen;
        },

        closeStartOverConfirmationModal() {
            this.isStartOverConfirmationModalOpen = false;
        },

        toggleQuitConfirmationModal() {
            this.isQuitConfirmationModalOpen = !this.isQuitConfirmationModalOpen;
        },

        closeQuitConfirmationModal() {
            this.isQuitConfirmationModalOpen = false;
        },

        toggleReviewConfirmationModal() {
            this.isReviewConfirmationModalOpen = !this.isReviewConfirmationModalOpen;
        },

        closeReviewConfirmationModal() {
            this.isReviewConfirmationModalOpen = false;
        },

        toggleReviewGroupTextModal() {
            this.isReviewGroupTextModalOpen = !this.isReviewGroupTextModalOpen;
        },

        closeReviewGroupTextModal() {
            this.isReviewGroupTextModalOpen = false;
        },

        toggleEnableBulkInformationModal() {
            this.isEnableBulkInformationModalOpen = !this.isEnableBulkInformationModalOpen;
        },

        closeEnableBulkInformationModal() {
            this.isEnableBulkInformationModalOpen = false;
        },

        confirmReview() {
            this.isReviewConfirmationModalOpen = false;

            const appStore = useAppStore();

            appStore.setCurrentStep(constants.STEP_REVIEW);
        },

        confirmQuit() {
            this.isQuitConfirmationModalOpen = false;

            this.globalCancel();
        },

        /**
         * Gets specific text section.
         */
        getTextSection(type, code) {
            const componentCode = `TextGallery-${type}-${code}`;

            const component = this.viewController.findComponent(componentCode);

            if (component) {
                const description = component.description[0].description;

                let html = micromark(description || '', {
                    extensions: [gfm()],
                    htmlExtensions: [gfmHtml()],
                });

                html = this.addTargetBlankToLinks(html);

                return html;
            }

            return '';
        },

        /**
         * Get help section of type.
         */
        getHelpSection(type) {
            const placement = this.viewController.findPlacement('TextGallery');

            const prefix = `TextGallery-${type}-`;

            const list = [];

            for (const component of placement.components) {
                if (component.code.startsWith(prefix) && this.viewController.isAvailable(component)) {
                    const name = component.description[0].name;
                    const description = component.description[0].description;

                    let html = micromark(description || '', {
                        extensions: [gfm()],
                        htmlExtensions: [gfmHtml()],
                    });

                    html = this.addTargetBlankToLinks(html);

                    list.push({
                        id: component.code,
                        title: name,
                        content: html,
                    });
                }
            }

            return list;
        },

        addTargetBlankToLinks(html) {
            const parser = new DOMParser();

            const doc = parser.parseFromString(html, 'text/html');

            const links = doc.querySelectorAll('a');

            links.forEach((link) => {
                if (link.href && !link.getAttribute('target')) {
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                }
            });

            return doc.body.innerHTML;
        },

        /**
         * Set quantity.
         */
        setQuantity(quantity) {
            this.quantity = quantity;

            this.calculatePrice();
        },

        /**
         * Clears placement.
         */
        clearPlacement(placementCode) {
            const controller = this.viewController;

            const selectedComponent = controller.selectedAtPlacement(placementCode);

            if (selectedComponent == null) {
                return;
            }

            if (selectedComponent.custom['dynamic-image-url'] && selectedComponent.custom['dynamic-image-url'].length > 0) {
                controller.updateDynamicImage(placementCode, {
                    title: '',
                    name: '',
                    url: '',
                    pngUrl: '',
                    jpgUrl: '',
                    width: '',
                    height: '',
                });
            }

            if (selectedComponent.custom['personalization-text'] && selectedComponent.custom['personalization-text'].length > 0) {
                controller.updatePersonalization(placementCode, '');
            }

            controller.clearComponent(placementCode);

            const selectedPlacement = controller.findPlacement(placementCode);

            controller.clearPlacementTags(placementCode);

            for (const c of selectedPlacement.components) {
                controller.clearComponentTags(c.code);
            }
        },

        clearIcon(placementCode) {
            const controller = this.viewController;

            this.cartSku = null;

            this.allowedSkus = {};

            const selectedComponent = controller.selectedAtPlacement(placementCode);

            // Clear custom attributes.
            if (selectedComponent?.custom?.['dynamic-image-gallery']) {
                controller.commitCustomAttributes(placementCode, {
                    'dynamic-image-gallery': '',
                });
            }
        },

        ...groupStore,
    },
});
